import { useSortable } from "@dnd-kit/sortable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DeleteIcon, DragHandleIcon } from "../../icons";
import AutomationStatusBadge from "./AutomationStatusBadge";
import "../../lists/ListRowActions.scss";
import { DragHandleProps, JourneyItem } from "./journeyPlanner.types";

function JourneyItemCardContent({
  canDrag = true,
  dragHandleAttributes,
  dragHandleListeners,
  dragHandleRef,
  item,
  index,
}: DragHandleProps & {
  canDrag?: boolean;
  item: JourneyItem;
  index: number;
}) {
  return (
    <div className="d-flex justify-content-between align-items-start gap-2">
      <div
        className={classNames(
          "drag-handle",
          "d-inline-flex",
          "align-items-center",
          "justify-content-center",
          "flex-shrink-0",
          "fs-6",
          {
            "opacity-50": !canDrag,
          }
        )}
        ref={canDrag ? dragHandleRef : undefined}
        title={
          canDrag
            ? "Move automation"
            : "Only one automation in this journey"
        }
        {...(canDrag ? dragHandleAttributes : {})}
        {...(canDrag ? dragHandleListeners : {})}
      >
        <FontAwesomeIcon icon={DragHandleIcon} />
      </div>
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
          <strong>
            {index + 1}. {item.AutomationName}
          </strong>
          <AutomationStatusBadge status={item.Status} />
        </div>
        {item.CommunicationName && (
          <div className="small text-muted">
            {item.CommunicationName}
          </div>
        )}
      </div>
    </div>
  );
}

function JourneyItemCard({
  canDrag = true,
  className,
  dragHandleAttributes,
  dragHandleListeners,
  dragHandleRef,
  index,
  item,
}: DragHandleProps & {
  canDrag?: boolean;
  className?: string;
  index: number;
  item: JourneyItem;
}) {
  return (
    <div
      className={classNames(
        "bg-white border rounded-2 py-3 pe-3 ps-2",
        className
      )}
    >
      <JourneyItemCardContent
        canDrag={canDrag}
        dragHandleAttributes={dragHandleAttributes}
        dragHandleListeners={dragHandleListeners}
        dragHandleRef={dragHandleRef}
        index={index}
        item={item}
      />
    </div>
  );
}

function SortableJourneyItem({
  canDrag = true,
  item,
  index,
  journeyId,
  onRemove,
}: {
  canDrag?: boolean;
  item: JourneyItem;
  index: number;
  journeyId: number;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transition,
    isDragging,
  } = useSortable({
    id: `journey-item-${item.Id}`,
    disabled: !canDrag,
    data: {
      type: "journey-item",
      index,
      item,
      itemId: item.Id,
      journeyId,
      label: item.AutomationName,
    },
  });

  const style = {
    transition,
  };

  return (
    <div
      className="d-flex align-items-start gap-2"
      ref={setNodeRef}
      style={style}
    >
      <div className="flex-grow-1">
        <JourneyItemCard
          canDrag={canDrag}
          className={isDragging ? "opacity-50" : undefined}
          dragHandleAttributes={attributes}
          dragHandleListeners={listeners}
          dragHandleRef={setActivatorNodeRef}
          index={index}
          item={item}
        />
      </div>
      {!isDragging && (
        <button
              className="list-row-action flex-shrink-0 hover-text-danger"
              onClick={onRemove}
              onPointerDown={(event) => event.stopPropagation()}
              title={`Remove ${item.AutomationName} from journey`}
              aria-label={`Remove ${item.AutomationName} from journey`}
              type="button"
            >
              <FontAwesomeIcon icon={DeleteIcon} />
            </button>
      )}
    </div>
  );
}

export { JourneyItemCard, JourneyItemCardContent };
export default SortableJourneyItem;
